import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Divider, Typography } from '@mui/material';
import RoundedBox from '@sharedCR/common/RoundedBox';
import FormGeneratorSwitchCDS from '@sharedCR/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { EscrowRequirementType } from 'constants/loanApplication';
import { checkIsDirectPendingChange } from 'utils/pendingChanges';

import {
  selectCommonInfoLoanApplication,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectImpoundTypes } from 'store/system/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { loanInfoConfigFields } from './LoanInfo.config';
import {
  escrowBlockStyles,
  escrowDividerStyles,
  escrowSwitchCustomStyles,
  escrowTitleStyles,
  insuranceEscrowsContStyles,
} from './LoanInfo.styles';

const defaultObject = {};

const EscrowsBlock = ({
  isLoading,
  form,
  validationErrors,
  handleChange,
  setInForm,
  pendingChanges,
  disabledPendingChangesHighlight,
  escrowSettings = defaultObject,
  isEscrowDisabled = false,
}) => {
  const { escrowRequirement } = useSelector(selectCommonInfoLoanApplication);
  const impoundsTypes = useSelector(selectImpoundTypes);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.LOAN_DETAILS));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.LOAN_DETAILS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.LOAN_DETAILS));

  const isDisabledByLock = isLoading || isPageLocked;

  const { insuranceEscrowSettings } = escrowSettings || {};
  const { homeownersInsuranceImpoundId, floodInsuranceImpoundId, insuranceImpoundsEscrowed } =
    insuranceEscrowSettings || {};

  const isFloodInsurancePresent = !!floodInsuranceImpoundId;
  const isDisabledWithFlood =
    isFloodInsurancePresent && escrowRequirement === EscrowRequirementType.OPTIONAL;

  const insuranceEscrowSwitches = useMemo(
    () =>
      (insuranceImpoundsEscrowed || []).map(
        ({ applicationInsuranceId, impoundTypeId, isEscrowed }) => ({
          impoundId: applicationInsuranceId,
          impoundTypeId,
          name: impoundsTypes[impoundTypeId]?.name,
          isEscrowed,
          isHomeowners: applicationInsuranceId === homeownersInsuranceImpoundId,
          isFlood: applicationInsuranceId === floodInsuranceImpoundId,
        }),
      ),
    [
      floodInsuranceImpoundId,
      homeownersInsuranceImpoundId,
      insuranceImpoundsEscrowed,
      impoundsTypes,
    ],
  );

  useEffect(() => {
    if (isLoading || !!form.escrowSettings?.isEscrowed || !isDisabledWithFlood) {
      return;
    }

    setInForm('escrowSettings.isEscrowed', true, true);
  }, [form.escrowSettings?.isEscrowed, isDisabledWithFlood, isLoading, setInForm]);

  const onEscrowSwitchChange = useCallback(
    e => {
      const isChecked = e.target.checked;

      if (!isChecked) {
        setInForm('escrowSettings.taxesEscrowSettings.isEscrowed', false, true);

        (insuranceImpoundsEscrowed || []).forEach(({ applicationInsuranceId }) => {
          setInForm(
            loanInfoConfigFields.isInsuranceEscrowed.nameInModel.replace(
              '[0]',
              `[${applicationInsuranceId}]`,
            ),
            e.target.checked,
            true,
          );
        });
      }

      if (isChecked && floodInsuranceImpoundId) {
        setInForm(
          loanInfoConfigFields.isInsuranceEscrowed.nameInModel.replace(
            '[0]',
            `[${floodInsuranceImpoundId}]`,
          ),
          e.target.checked,
          true,
        );
      }

      handleChange(e);
    },
    [floodInsuranceImpoundId, insuranceImpoundsEscrowed, setInForm, handleChange],
  );

  return (
    <RoundedBox customStyles={escrowBlockStyles}>
      <FormGeneratorSwitchCDS
        checked={!!form.escrowSettings?.isEscrowed}
        customStyles={escrowSwitchCustomStyles}
        data-testid={PropertyAndLoanTestIds.LISTING_AGENT_FORM_ESCROW_SETTINGS_IS_ESCROWED_SWITCH}
        disabled={
          isDisabledByLock || isEscrowDisabled || isDisabledWithFlood || lockedFieldsMap.isEscrowed
        }
        error={!!validationErrors.escrowSettings?.isEscrowed}
        helperText={validationErrors.escrowSettings?.isEscrowed}
        id="escrowSettings.isEscrowed"
        isFieldChanged={
          !disabledPendingChangesHighlight &&
          checkIsDirectPendingChange(_.get(pendingChanges, 'escrowSettings.isEscrowed'))
        }
        isHidden={hiddenFieldsMap.isEscrowed}
        label="Escrow:"
        name="escrowSettings.isEscrowed"
        onChange={onEscrowSwitchChange}
      />

      <Divider className="escrow-divider" sx={escrowDividerStyles} />

      <Typography component="p" sx={escrowTitleStyles} variant="medium14">
        Escrow Selections
      </Typography>

      <FormGeneratorSwitchCDS
        checked={!!form.escrowSettings?.taxesEscrowSettings?.isEscrowed}
        customStyles={escrowSwitchCustomStyles}
        data-testid={
          PropertyAndLoanTestIds.LISTING_AGENT_FORM_ESCROW_SETTINGS_TAXES_IS_ESCROWED_SWITCH
        }
        disabled={
          isDisabledByLock ||
          isEscrowDisabled ||
          !form.escrowSettings?.isEscrowed ||
          lockedFieldsMap.isTaxesEscrowed
        }
        error={!!validationErrors.escrowSettings?.taxesEscrowSettings?.isEscrowed}
        helperText={validationErrors.escrowSettings?.taxesEscrowSettings?.isEscrowed}
        id="escrowSettings.taxesEscrowSettings.isEscrowed"
        isFieldChanged={
          !disabledPendingChangesHighlight &&
          checkIsDirectPendingChange(
            _.get(pendingChanges, 'escrowSettings.taxesEscrowSettings.isEscrowed'),
          )
        }
        isHidden={hiddenFieldsMap.isTaxesEscrowed}
        label="Taxes:"
        name="escrowSettings.taxesEscrowSettings.isEscrowed"
        onChange={handleChange}
      />

      <Box sx={insuranceEscrowsContStyles}>
        {insuranceEscrowSwitches.map(({ impoundId, name, isFlood }) => (
          <FormGeneratorSwitchCDS
            checked={!!form.escrowSettings?.insuranceEscrowSettings?.[impoundId]?.isEscrowed}
            customStyles={escrowSwitchCustomStyles}
            data-testid={PropertyAndLoanTestIds.getImpoundIsEscrowedId(impoundId)}
            disabled={
              isDisabledByLock ||
              isEscrowDisabled ||
              isFlood ||
              !form.escrowSettings?.isEscrowed ||
              lockedFieldsMap.isInsuranceEscrowed
            }
            error={
              !!validationErrors.escrowSettings?.insuranceEscrowSettings?.[impoundId]?.isEscrowed
            }
            helperText={
              validationErrors.escrowSettings?.insuranceEscrowSettings?.[impoundId]?.isEscrowed
            }
            id={`escrowSettings.insuranceEscrowSettings[${impoundId}].isEscrowed`}
            isFieldChanged={
              !disabledPendingChangesHighlight &&
              checkIsDirectPendingChange(
                _.get(
                  pendingChanges,
                  `escrowSettings.insuranceEscrowSettings[${impoundId}].isEscrowed`,
                ),
              )
            }
            key={impoundId}
            label={`${isFlood ? 'Flood' : name}:`}
            name={`escrowSettings.insuranceEscrowSettings[${impoundId}].isEscrowed`}
            onChange={handleChange}
          />
        ))}
      </Box>
    </RoundedBox>
  );
};

EscrowsBlock.propTypes = {
  ...WithLoadingPropTypes,
  form: PropTypes.object.isRequired,
  validationErrors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object.isRequired,
  disabledPendingChangesHighlight: PropTypes.bool.isRequired,
  escrowSettings: PropTypes.object,
  isEscrowDisabled: PropTypes.bool,
};

export default memo(EscrowsBlock);
